import { datadogRum } from '@datadog/browser-rum';
import {
  CloseEvent,
  ErrorEvent,
  ResponseAudioEvent,
} from '@pages/interview/clients/chat-client';
import { isTemporaryUnavailableError } from '@pages/interview/temporary-unavailability';
import * as Sentry from '@sentry/react';
import { useCallback, useMemo } from 'react';
import { InteractionState, NetworkState } from '../pages/interview/types';
import type { AudioIdentity } from '../services/audio-flow';

export const useInterviewHooks = ({
  interviewId,
  setInteractionState,
  onCompleted,
  play,
  interrupt,
  sendPlaybackComplete,
  stopRecording,
  setNetworkState,
  setShowConnectionRecoveredMessage,
  setZaraBusyError,
  setResumeError,
  setShouldStartCompleting,
  setIsComplete,
}: {
  interviewId: string;
  setInteractionState: (state: InteractionState) => void;
  onCompleted: () => void;
  play?: (audio: ArrayBuffer, identity?: AudioIdentity) => void;
  interrupt?: () => void;
  sendPlaybackComplete?: () => void;
  stopRecording: () => Promise<void>;
  setNetworkState: (state: NetworkState) => void;
  setShowConnectionRecoveredMessage: (show: boolean) => void;
  setZaraBusyError: (error: boolean) => void;
  setResumeError: (error: boolean) => void;
  setShouldStartCompleting: (should: boolean) => void;
  setIsComplete: (complete: boolean) => void;
}) => {
  const onResponseAudio = useCallback(
    ({
      audio,
      responseId,
      generation,
      providerConnectionEpoch,
    }: ResponseAudioEvent) => {
      setInteractionState('aiSpeaking');
      play?.(audio, { responseId, generation, providerConnectionEpoch });
    },
    [play, setInteractionState],
  );

  const onResponseInterrupted = useCallback(() => {
    setInteractionState('recording');
    interrupt?.();
    // avService.interrupt() clears the queue but does NOT fire
    // onplaybackcomplete, so signal the server explicitly here.
    sendPlaybackComplete?.();
  }, [interrupt, sendPlaybackComplete, setInteractionState]);

  const onConnectionUnstable = useCallback(() => {
    setNetworkState('unstable');
    console.log('Connection unstable...');

    datadogRum.addAction('connection_unstable', {
      visibilityState: document.visibilityState,
    });
  }, [setNetworkState]);

  const onConnectionLost = useCallback(() => {
    interrupt?.();
    stopRecording();
    setNetworkState('lost');
    setInteractionState('error');
    Sentry.captureMessage('Connection lost', {
      tags: { manualCapture: true },
      extra: {
        interviewId,
        visibilityState: document.visibilityState,
      },
    });
    console.log('Connection lost.');

    datadogRum.addError('Connection lost', {
      visibilityState: document.visibilityState,
    });
  }, [
    interviewId,
    interrupt,
    stopRecording,
    setNetworkState,
    setInteractionState,
  ]);

  const onConnectionRecovered = useCallback(() => {
    setNetworkState('stable');
    setShowConnectionRecoveredMessage(true);
    setTimeout(() => setShowConnectionRecoveredMessage(false), 3000);
    console.log('Connection recovered.');

    datadogRum.addAction('connection_recovered', {
      visibilityState: document.visibilityState,
    });
  }, [setNetworkState, setShowConnectionRecoveredMessage]);

  const onChatClientError = useCallback(
    (event: ErrorEvent) => {
      if (isTemporaryUnavailableError(event.error)) {
        setZaraBusyError(true);
      } else if (
        event.error.type === 'unprocessable_content_error' &&
        event.error.message.includes('Resume text is not valid UTF-8')
      ) {
        setResumeError(true);
      } else {
        setInteractionState('error');
        console.error('Chat client error:', event.error);
      }

      datadogRum.addError('Interview API error', {
        type: event.error.type,
        message: event.error.message,
      });
    },
    [setZaraBusyError, setResumeError, setInteractionState],
  );

  const onChatClientClosed = useCallback((event: CloseEvent) => {
    if (event.wasClean === false) {
      datadogRum.addError('Chat client connection closed abnormally', {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
      });
    }
  }, []);

  const onChatClientComplete = useCallback(() => {
    // The server is telling us to complete the interview. Trigger the
    // completion process...
    setShouldStartCompleting(true);

    datadogRum.addAction('should_complete_interview');
  }, [setShouldStartCompleting]);

  const onChatClientCompleted = useCallback(() => {
    datadogRum.addAction('interview_completed');

    setIsComplete(true);
    onCompleted();
  }, [setIsComplete, onCompleted]);

  return useMemo(() => {
    return {
      onResponseAudio,
      onResponseInterrupted,
      onConnectionUnstable,
      onConnectionLost,
      onConnectionRecovered,
      onChatClientError,
      onChatClientClosed,
      onChatClientComplete,
      onChatClientCompleted,
    };
  }, [
    onResponseAudio,
    onResponseInterrupted,
    onConnectionUnstable,
    onConnectionLost,
    onConnectionRecovered,
    onChatClientError,
    onChatClientClosed,
    onChatClientComplete,
    onChatClientCompleted,
  ]);
};
